// Импорт напрямую из `model/types.ts`, а не из публичного индекса entity, — индекс
// реэкспортирует Vue-компоненты (`InventTypeField.vue`), которые тянут Vuetify
// и не резолвятся в node-окружении vitest. Тот же приём используют другие
// spec-файлы рядом с `entities/Inventory/TypeField/model/*`.
import { ComputedConditionList, TextFieldList } from '@/entities/Inventory/TypeField/model/types.ts'

/**
 * Типы полей, не поддерживаемые расширенным поиском:
 * - `link_object` / `object_link` — ссылка на объект. Бэкенд принимает фильтр только
 *   по внутреннему id связанного объекта, ввести который руками нельзя; UI-виджета
 *   выбора объекта для условия фильтра сейчас нет.
 * - `link_file` — файл, сравнивать значение файла бессмысленно.
 * - `computed` — вычисляемое поле. Значение не попадает в выгрузку для поиска
 *   (`GET /inventory/objects/search-data`), поэтому любое условие по нему всегда
 *   возвращает пустой результат.
 *
 * `link_object` — реальное значение из API (см. `types_full.json`), `object_link` —
 * значение из `ReferenceFieldList` в `@/entities/Inventory/TypeField`. Оставляем оба,
 * чтобы не сломаться, если бэкенд когда-нибудь приведёт их к единому виду.
 */
const SEARCH_UNSUPPORTED_FIELD_TYPES = new Set<string>(['link_object', 'object_link', 'link_file', 'computed'])

/**
 * Отфильтровывает поля типа объекта до тех, что можно использовать в условии
 * расширенного поиска (см. `SEARCH_UNSUPPORTED_FIELD_TYPES`).
 */
export const filterSearchableFields = <T extends { type: string }>(fields: T[]): T[] =>
  fields.filter((field) => !SEARCH_UNSUPPORTED_FIELD_TYPES.has(field.type))

/**
 * Список условий фильтра, применимых для конкретного типа поля в расширенном поиске.
 *
 * Отдельно от общего `ComputedConditionList` исключаем «Больше или равно» / «Меньше
 * или равно» для текстовых полей: search-сервис сравнивает такие значения не как
 * ожидается (пустые значения совпадают с любым условием, сравнение может быть
 * инвертированным), поэтому для текста оставляем только «Равно» и «Содержит строку».
 * Общий `ComputedConditionList` не трогаем — он используется в отчётах и настройке
 * вычисляемых полей паспорта, где эти условия работают по-другому.
 */
export const getSearchConditionsForFieldType = (fieldType: string) => {
  const isTextField = (Object.values(TextFieldList) as string[]).includes(fieldType)

  return ComputedConditionList.filter((condition) => {
    if (!(condition.accept_fields as string[]).includes(fieldType)) return false
    if (isTextField && (condition.value === 'MORE' || condition.value === 'LESS')) return false
    return true
  })
}

/**
 * Условия для псевдо-размещения (props:<id>): только IN_LIST — бэкенд матчит
 * результат pseudo_sql по именам выбранных элементов связанного справочника.
 * Список показываем в селекте условия, как у обычного справочного поля.
 */
export const getSearchConditionsForPseudoField = () =>
  ComputedConditionList.filter((condition) => condition.value === 'IN_LIST')
